import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  CircularProgress,
  Stack,
  Tooltip,
  InputAdornment,
  useTheme,
  alpha
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  AccountBalance as AccountBalanceIcon,
  CalendarMonth as CalendarIcon,
  Description as DescriptionIcon,
  Receipt as ReceiptIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { getGLAccounts, GLAccount } from '../firebase/glAccountServices';
import { createGLTransaction, GLTransaction, GLTransactionEntry } from '../firebase/glTransactionServices';

// Interface for journal entry line
interface JournalLine {
  id: string;
  accountId: string;
  accountNumber: string;
  accountName: string;
  debit: number;
  credit: number;
}

const JournalEntry: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  // State for journal entry
  const [transactionDate, setTransactionDate] = useState<Date | null>(new Date());
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');
  const [journalLines, setJournalLines] = useState<JournalLine[]>([
    {
      id: crypto.randomUUID(),
      accountId: '',
      accountNumber: '',
      accountName: '',
      debit: 0,
      credit: 0
    }
  ]);
  
  // State for GL accounts
  const [accounts, setAccounts] = useState<GLAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Calculate totals
  const totalDebit = journalLines.reduce((sum, line) => sum + (line.debit || 0), 0);
  const totalCredit = journalLines.reduce((sum, line) => sum + (line.credit || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01; // Allow for small rounding differences
  
  // Load GL accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      setLoading(true);
      try {
        const accountsData = await getGLAccounts();
        // Filter for active accounts and sort by account number
        const activeAccounts = accountsData
          .filter((account: GLAccount) => account.isActive && account.level === 3) // Only include detail accounts
          .sort((a: GLAccount, b: GLAccount) => a.accountNumber.localeCompare(b.accountNumber));
        setAccounts(activeAccounts);
      } catch (error) {
        console.error('Error fetching GL accounts:', error);
        enqueueSnackbar('Failed to load GL accounts', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    
    fetchAccounts();
  }, [enqueueSnackbar]);
  
  // Handle adding a new line
  const handleAddLine = () => {
    setJournalLines([
      ...journalLines,
      {
        id: crypto.randomUUID(),
        accountId: '',
        accountNumber: '',
        accountName: '',
        debit: 0,
        credit: 0
      }
    ]);
  };
  
  // Handle removing a line
  const handleRemoveLine = (id: string) => {
    if (journalLines.length <= 1) {
      enqueueSnackbar('Journal entry must have at least one line', { variant: 'warning' });
      return;
    }
    setJournalLines(journalLines.filter(line => line.id !== id));
  };
  
  // Handle account selection
  const handleAccountChange = (index: number, account: GLAccount | null) => {
    if (!account) return;
    
    const updatedLines = [...journalLines];
    updatedLines[index] = {
      ...updatedLines[index],
      accountId: account.id || '',
      accountNumber: account.accountNumber,
      accountName: account.accountName
    };
    setJournalLines(updatedLines);
  };
  
  // Handle debit amount change
  const handleDebitChange = (index: number, value: string) => {
    const amount = parseFloat(value) || 0;
    const updatedLines = [...journalLines];
    updatedLines[index] = {
      ...updatedLines[index],
      debit: amount,
      credit: amount > 0 ? 0 : updatedLines[index].credit // Clear credit if debit is entered
    };
    setJournalLines(updatedLines);
  };
  
  // Handle credit amount change
  const handleCreditChange = (index: number, value: string) => {
    const amount = parseFloat(value) || 0;
    const updatedLines = [...journalLines];
    updatedLines[index] = {
      ...updatedLines[index],
      credit: amount,
      debit: amount > 0 ? 0 : updatedLines[index].debit // Clear debit if credit is entered
    };
    setJournalLines(updatedLines);
  };
  
  // Save journal entry
  const handleSave = async () => {
    // Validate inputs
    if (!transactionDate) {
      enqueueSnackbar('Please select a transaction date', { variant: 'warning' });
      return;
    }
    
    if (!description.trim()) {
      enqueueSnackbar('Please enter a description', { variant: 'warning' });
      return;
    }
    
    // Check if all lines have an account selected
    const hasEmptyAccount = journalLines.some(line => !line.accountId);
    if (hasEmptyAccount) {
      enqueueSnackbar('All lines must have an account selected', { variant: 'warning' });
      return;
    }
    
    // Check if all lines have either a debit or credit amount
    const hasEmptyAmount = journalLines.some(line => line.debit === 0 && line.credit === 0);
    if (hasEmptyAmount) {
      enqueueSnackbar('All lines must have either a debit or credit amount', { variant: 'warning' });
      return;
    }
    
    // Check if journal is balanced
    if (!isBalanced) {
      enqueueSnackbar('Journal entry must be balanced (total debits must equal total credits)', { variant: 'warning' });
      return;
    }
    
    // Create transaction entries
    const entries: GLTransactionEntry[] = journalLines.map(line => ({
      accountId: line.accountId,
      accountNumber: line.accountNumber,
      accountName: line.accountName,
      debit: line.debit,
      credit: line.credit
    }));
    
    // Create transaction object
    const transaction: Omit<GLTransaction, 'id'> = {
      transactionDate,
      description,
      referenceType: 'journal',
      referenceId: reference || '',
      entries
    };
    
    // Save transaction
    setSaving(true);
    try {
      const transactionId = await createGLTransaction(transaction);
      enqueueSnackbar('Journal entry saved successfully', { variant: 'success' });
      
      // Reset form
      setTransactionDate(new Date());
      setDescription('');
      setReference('');
      setJournalLines([
        {
          id: crypto.randomUUID(),
          accountId: '',
          accountNumber: '',
          accountName: '',
          debit: 0,
          credit: 0
        }
      ]);
      
      // Navigate to transaction view
      navigate(`/gl-transactions/${transactionId}`);
    } catch (error) {
      console.error('Error saving journal entry:', error);
      enqueueSnackbar('Failed to save journal entry', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };
  
  // Format currency for display
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 2
    }).format(amount);
  };
  
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton 
            color="primary" 
            onClick={() => navigate('/journal')} 
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h1" fontWeight={600}>
            <AccountBalanceIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
            Manual Journal Entry
          </Typography>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Paper sx={{ mb: 3, p: 3 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                <Box sx={{ flex: 1 }}>
                  <DatePicker
                    label="Transaction Date"
                    value={transactionDate}
                    onChange={(newValue) => setTransactionDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        InputProps: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarIcon />
                            </InputAdornment>
                          )
                        }
                      }
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    label="Reference"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ReceiptIcon />
                        </InputAdornment>
                      )
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DescriptionIcon />
                        </InputAdornment>
                      )
                    }}
                  />
                </Box>
              </Stack>
            </Paper>
            
            <Paper sx={{ mb: 3 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                      <TableCell width="45%">Account</TableCell>
                      <TableCell align="right" width="20%">Debit</TableCell>
                      <TableCell align="right" width="20%">Credit</TableCell>
                      <TableCell align="center" width="15%">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {journalLines.map((line, index) => (
                      <TableRow key={line.id}>
                        <TableCell>
                          <Autocomplete
                            options={accounts}
                            getOptionLabel={(option) => `${option.accountNumber} - ${option.accountName}`}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            onChange={(_, newValue) => handleAccountChange(index, newValue)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select Account"
                                required
                                size="small"
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={line.debit || ''}
                            onChange={(e) => handleDebitChange(index, e.target.value)}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">RM</InputAdornment>,
                            }}
                            size="small"
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={line.credit || ''}
                            onChange={(e) => handleCreditChange(index, e.target.value)}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">RM</InputAdornment>,
                            }}
                            size="small"
                            fullWidth
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveLine(line.id)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {/* Totals row */}
                    <TableRow sx={{ backgroundColor: alpha(theme.palette.grey[100], 0.5) }}>
                      <TableCell>
                        <Typography variant="subtitle2">Totals</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle2" color={isBalanced ? 'success.main' : 'error.main'}>
                          {formatCurrency(totalDebit)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle2" color={isBalanced ? 'success.main' : 'error.main'}>
                          {formatCurrency(totalCredit)}
                        </Typography>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                    
                    {/* Difference row */}
                    {!isBalanced && (
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.error.light, 0.1) }}>
                        <TableCell>
                          <Typography variant="subtitle2" color="error.main">Difference</Typography>
                        </TableCell>
                        <TableCell align="right" colSpan={2}>
                          <Typography variant="subtitle2" color="error.main">
                            {formatCurrency(Math.abs(totalDebit - totalCredit))}
                          </Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    )}
                    
                    {/* Add line button */}
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ border: 'none', pt: 2, pb: 2 }}>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={handleAddLine}
                        >
                          Add Line
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={!isBalanced || saving}
              >
                {saving ? 'Saving...' : 'Save Journal Entry'}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default JournalEntry;
